import React, { useState, useEffect } from "react";
import { Modal, Flex, Typography, Input, Image, Spin, Card, Button, Popconfirm, message } from "antd";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../api/emptyV2Api";
import { useDeleteWebshopAssetMutation } from "../../../api/webshop/wsProductsApi";

const { Text } = Typography;

const dev_assets_url =
  "https://webshop-assets-20260531210740877800000001.s3.amazonaws.com/";
const prod_assets_url =
  "https://webshop-assets-20260531232150401200000002.s3.amazonaws.com/";

const S3_BUCKET_URL =
  process.env.NODE_ENV === "production" ||
  process.env.VERCEL_ENV === "production"
    ? prod_assets_url
    : dev_assets_url;

export interface AssetFile {
  key: string;
  url?: string;
}

interface AssetPopupProps {
  open: boolean;
  onClose: () => void;
  onSelect: (filename: string) => void;
  prefix: "products/" | "network/";
}

export default function AssetPopup({
  open,
  onClose,
  onSelect,
  prefix,
}: AssetPopupProps) {
  const [files, setFiles] = useState<AssetFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [deleteWebshopAsset] = useDeleteWebshopAssetMutation();

  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const folder = prefix.replace("/", "");
      const response = await fetch(
        `${BASE_URL}webshop/products/manage?action=webshop-assets&folder=${folder}`,
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setFiles(data);
    } catch (err: any) {
      console.error("Error listing files from S3 via API:", err);
      setError(err?.message || "Failed to fetch images from S3");
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "test"
      ) {
        setFiles([
          { key: `${prefix}thailand-daily-plan.png` },
          { key: `${prefix}singapore-unlimited.png` },
          { key: `${prefix}malaysia-travel-sim.png` },
          { key: `${prefix}indonesia-data-pack.png` },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFiles();
      setSearchTerm("");
    }
  }, [open, prefix]);

  const handleDeleteAsset = (file: AssetFile) => {
    setDeletingKey(file.key);
    return deleteWebshopAsset({ key: file.key })
      .unwrap()
      .then((res) => {
        message.success(res.message || `Deleted ${res.deleted?.[0] || file.key}`);
        setFiles((prev) => prev.filter((item) => item.key !== file.key));
        fetchFiles();
      })
      .catch((err) => {
        console.error("API error:", err);
        message.error(
          err?.data?.message ||
            (typeof err?.data === "string" ? err.data : null) ||
            err?.error ||
            err?.message ||
            "Failed to delete asset",
        );
      })
      .finally(() => {
        setDeletingKey(null);
      });
  };

  const filteredFiles = files.filter((file) => {
    const filename = file.key.substring(file.key.lastIndexOf("/") + 1);
    return filename.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Modal
      title={`Select Asset (${prefix === "products/" ? "Products" : "Networks"})`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Flex vertical gap={16}>
        <Input
          placeholder="Search assets by filename..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
        />

        {loading ? (
          <Flex justify="center" style={{ padding: "40px" }}>
            <Spin />
          </Flex>
        ) : (
          <div
            style={{ maxHeight: "400px", overflowY: "auto", padding: "4px" }}
          >
            {error && (
              <div
                style={{
                  color: "#cf1322",
                  backgroundColor: "#fff1f0",
                  border: "1px solid #ffa39e",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  marginBottom: "16px",
                }}
              >
                <Text type="danger">
                  {error}. Using local development mock asset fallbacks for
                  testing the interface.
                </Text>
              </div>
            )}

            <Flex wrap="wrap" gap={12}>
              {filteredFiles.map((file) => {
                const filename = file.key.substring(
                  file.key.lastIndexOf("/") + 1,
                );
                const imgUrl = file.url || `${S3_BUCKET_URL}${file.key}`;
                return (
                  <Card
                    key={file.key}
                    hoverable
                    bodyStyle={{ padding: "8px", textAlign: "center" }}
                    style={{
                      width: "140px",
                      cursor: "pointer",
                      border: "1px solid #e8e8e8",
                      position: "relative",
                    }}
                    onClick={() => onSelect(filename)}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        zIndex: 1,
                      }}
                    >
                      <Popconfirm
                        title="Delete this asset from S3?"
                        description="This does not unassign imageUrl on products."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDeleteAsset(file)}
                      >
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={deletingKey === file.key}
                        />
                      </Popconfirm>
                    </div>
                    <Image
                      src={imgUrl}
                      alt={filename}
                      width={120}
                      height={120}
                      style={{ objectFit: "cover", borderRadius: "4px" }}
                      preview={false}
                      fallback="https://placehold.co/120?text=Asset"
                    />
                    <Text
                      ellipsis
                      style={{
                        display: "block",
                        marginTop: "8px",
                        fontSize: "12px",
                      }}
                      title={filename}
                    >
                      {filename}
                    </Text>
                  </Card>
                );
              })}
              {filteredFiles.length === 0 && (
                <Text
                  type="secondary"
                  style={{
                    width: "100%",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  No assets found.
                </Text>
              )}
            </Flex>
          </div>
        )}
      </Flex>
    </Modal>
  );
}
